/**
 * Опись инсталляции: серверы / интеграции / контакты.
 * Используется в карточке инсталляции (раскрывающийся блок).
 */
import { useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, Save, X, Eye, EyeOff, Server, Cable, Users } from 'lucide-react';
import {
  instInvApi, SERVER_ROLE_LABEL,
  type InstServer, type ServerRole,
  type InstIntegration, type InstContact,
} from '../api/installation_inventory';

const ROLE_KEYS: ServerRole[] = [
  'proxy', 'search', 'mgmt', 'app', 'broker', 'db',
  'camunda', 'customer_smtp', 'customer_ad', 'other',
];

export default function InstallationInventoryPanel({ instId }: { instId: string }) {
  const [tab, setTab] = useState<'servers' | 'integrations' | 'contacts'>('servers');
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl">
      <nav className="flex gap-1 px-3 py-2 border-b border-slate-800">
        <TabBtn active={tab === 'servers'} onClick={() => setTab('servers')}
                icon={<Server size={13} />} label="Серверы" />
        <TabBtn active={tab === 'integrations'} onClick={() => setTab('integrations')}
                icon={<Cable size={13} />} label="Интеграции" />
        <TabBtn active={tab === 'contacts'} onClick={() => setTab('contacts')}
                icon={<Users size={13} />} label="Контакты" />
      </nav>
      <div className="p-3">
        {tab === 'servers' && <ServersSection instId={instId} />}
        {tab === 'integrations' && <IntegrationsSection instId={instId} />}
        {tab === 'contacts' && <ContactsSection instId={instId} />}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon, label }:
  { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs font-bold uppercase transition ${
        active ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
      }`}>{icon} {label}</button>
  );
}

// ── Servers ────────────────────────────────────────────────────────────────
function ServersSection({ instId }: { instId: string }) {
  const [items, setItems] = useState<InstServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState(false);
  const [editing, setEditing] = useState<InstServer | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await instInvApi.servers(instId, reveal)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [instId, reveal]);

  const remove = async (s: InstServer) => {
    if (!window.confirm(`Удалить ${SERVER_ROLE_LABEL[s.role]} ${s.name || s.ip}?`)) return;
    await instInvApi.serverDelete(instId, s.id); load();
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
          <input type="checkbox" checked={reveal} onChange={e => setReveal(e.target.checked)} />
          {reveal ? <Eye size={12} /> : <EyeOff size={12} />} показать пароли
        </label>
        <button onClick={() => setEditing('new')}
          className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded text-xs font-bold">
          <Plus size={12} /> Сервер
        </button>
      </div>
      {loading ? <Loader2 className="animate-spin text-blue-400 mx-auto my-4" /> :
       items.length === 0 ? (
         <div className="text-xs text-slate-500 text-center py-4">Серверы не добавлены.</div>
       ) : (
         <div className="space-y-2">
           {items.map(s => (
             <div key={s.id} className="bg-slate-950 border border-slate-800 rounded p-2.5 text-xs">
               <header className="flex justify-between items-baseline mb-1">
                 <div>
                   <span className="inline-block px-1.5 py-0.5 rounded bg-purple-900/40 text-purple-300 text-[10px] font-bold uppercase mr-2">
                     {SERVER_ROLE_LABEL[s.role]}
                   </span>
                   <b className="text-slate-200">{s.name || '—'}</b>
                   {s.ip && <span className="ml-2 text-slate-500 font-mono">{s.ip}{s.port ? `:${s.port}` : ''}</span>}
                 </div>
                 <div className="flex gap-1">
                   <button onClick={() => setEditing(s)} className="text-slate-500 hover:text-blue-400 p-1">
                     <Pencil size={11} />
                   </button>
                   <button onClick={() => remove(s)} className="text-slate-500 hover:text-red-400 p-1">
                     <Trash2 size={11} />
                   </button>
                 </div>
               </header>
               <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-0.5 text-[11px] text-slate-400">
                 {s.os && <div><dt className="inline text-slate-600">OS:</dt> {s.os} {s.os_version}</div>}
                 {(s.vcpu || s.ram_gb || s.disk_gb) && (
                   <div><dt className="inline text-slate-600">HW:</dt> {s.vcpu || '?'} vCPU / {s.ram_gb || '?'}G / {s.disk_gb || '?'}G</div>
                 )}
                 {s.ssh_user && <div><dt className="inline text-slate-600">SSH:</dt> {s.ssh_user} <span className="font-mono text-slate-500">{s.ssh_password}</span></div>}
                 {s.smtp_user && <div><dt className="inline text-slate-600">SMTP:</dt> {s.smtp_user} <span className="font-mono text-slate-500">{s.smtp_password}</span></div>}
                 {s.auth_user && <div><dt className="inline text-slate-600">AUTH:</dt> {s.auth_user}@{s.auth_domain || ''} <span className="font-mono text-slate-500">{s.auth_password}</span></div>}
                 {s.notes && <div className="col-span-full text-slate-400 italic">{s.notes}</div>}
               </dl>
             </div>
           ))}
         </div>
       )}
      {editing && (
        <ServerModal instId={instId} server={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function ServerModal({ instId, server, onClose, onSaved }:
  { instId: string; server: InstServer | null; onClose: () => void; onSaved: () => void }) {
  const empty = {
    role: 'app' as ServerRole, name: '', ip: '', port: null as number | null,
    os: '', os_version: '', vcpu: null as number | null, ram_gb: null as number | null, disk_gb: null as number | null,
    ssh_user: '', ssh_password: '', ssh_key: '',
    smtp_user: '', smtp_password: '',
    auth_user: '', auth_password: '', auth_domain: '',
    notes: '',
  };
  const [form, setForm] = useState<any>(server ? { ...empty, ...server } : empty);
  const [busy, setBusy] = useState(false);
  const upd = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload: any = { ...form };
      ['port', 'vcpu', 'ram_gb', 'disk_gb'].forEach(k => {
        if (payload[k] === '' || payload[k] === null) payload[k] = null;
        else payload[k] = Number(payload[k]);
      });
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      if (server) await instInvApi.serverUpdate(instId, server.id, payload);
      else await instInvApi.serverCreate(instId, payload);
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10" onClick={onClose}>
      <form onSubmit={submit} className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl mx-4 shadow-2xl"
            onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between">
          <h3 className="text-lg font-bold text-white">{server ? 'Изменить сервер' : 'Новый сервер'}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </header>
        <div className="p-4 grid grid-cols-2 gap-3">
          <Fld label="Роль *">
            <select value={form.role} onChange={e => upd('role', e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white">
              {ROLE_KEYS.map(r => <option key={r} value={r}>{SERVER_ROLE_LABEL[r]}</option>)}
            </select>
          </Fld>
          <Fld label="Имя"><Inp v={form.name} k="name" upd={upd} placeholder="proxy-01" /></Fld>
          <Fld label="IP"><Inp v={form.ip} k="ip" upd={upd} placeholder="10.0.0.1" /></Fld>
          <Fld label="Port"><Inp v={form.port} k="port" upd={upd} type="number" /></Fld>
          <Fld label="OS"><Inp v={form.os} k="os" upd={upd} placeholder="Astra Linux" /></Fld>
          <Fld label="OS version"><Inp v={form.os_version} k="os_version" upd={upd} placeholder="1.8.1" /></Fld>
          <Fld label="vCPU"><Inp v={form.vcpu} k="vcpu" upd={upd} type="number" /></Fld>
          <Fld label="RAM (GB)"><Inp v={form.ram_gb} k="ram_gb" upd={upd} type="number" /></Fld>
          <Fld label="Disk (GB)"><Inp v={form.disk_gb} k="disk_gb" upd={upd} type="number" /></Fld>
          <div />
          <Fld label="SSH user"><Inp v={form.ssh_user} k="ssh_user" upd={upd} /></Fld>
          <Fld label="SSH password"><Inp v={form.ssh_password} k="ssh_password" upd={upd} type="password" /></Fld>
          <Fld label="SSH key (опц., PEM)" colSpan={2}>
            <textarea value={form.ssh_key || ''} onChange={e => upd('ssh_key', e.target.value)} rows={3}
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white font-mono" />
          </Fld>
          {(form.role === 'customer_smtp' || form.smtp_user) && (
            <>
              <Fld label="SMTP user"><Inp v={form.smtp_user} k="smtp_user" upd={upd} /></Fld>
              <Fld label="SMTP password"><Inp v={form.smtp_password} k="smtp_password" upd={upd} type="password" /></Fld>
            </>
          )}
          {(form.role === 'customer_ad' || form.auth_user) && (
            <>
              <Fld label="AUTH user"><Inp v={form.auth_user} k="auth_user" upd={upd} /></Fld>
              <Fld label="AUTH password"><Inp v={form.auth_password} k="auth_password" upd={upd} type="password" /></Fld>
              <Fld label="AUTH domain" colSpan={2}><Inp v={form.auth_domain} k="auth_domain" upd={upd} placeholder="corp.client.ru" /></Fld>
            </>
          )}
          <Fld label="Заметки" colSpan={2}>
            <textarea value={form.notes || ''} onChange={e => upd('notes', e.target.value)} rows={2}
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white" />
          </Fld>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-800">
          <button type="button" onClick={onClose} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded text-sm">Отмена</button>
          <button type="submit" disabled={busy}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white rounded text-sm font-bold inline-flex items-center gap-1.5">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Сохранить
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Integrations ───────────────────────────────────────────────────────────
function IntegrationsSection({ instId }: { instId: string }) {
  const [items, setItems] = useState<InstIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState(false);
  const [editing, setEditing] = useState<InstIntegration | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await instInvApi.integrations(instId, reveal)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [instId, reveal]);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
          <input type="checkbox" checked={reveal} onChange={e => setReveal(e.target.checked)} />
          {reveal ? <Eye size={12} /> : <EyeOff size={12} />} показать пароли
        </label>
        <button onClick={() => setEditing('new')}
          className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded text-xs font-bold">
          <Plus size={12} /> Интеграция
        </button>
      </div>
      {loading ? <Loader2 className="animate-spin text-blue-400 mx-auto my-4" /> :
       items.length === 0 ? (
         <div className="text-xs text-slate-500 text-center py-4">Интеграций нет.</div>
       ) : (
         <div className="space-y-2">
           {items.map(i => (
             <div key={i.id} className="bg-slate-950 border border-slate-800 rounded p-2.5 text-xs">
               <header className="flex justify-between items-baseline">
                 <div>
                   {i.kind && <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300 text-[10px] font-bold uppercase mr-2">{i.kind}</span>}
                   <b className="text-slate-200">{i.name}</b>
                   {i.version && <span className="ml-1 text-slate-500">v{i.version}</span>}
                   {i.ip && <span className="ml-2 text-slate-500 font-mono">{i.ip}{i.port ? `:${i.port}` : ''}</span>}
                 </div>
                 <div className="flex gap-1">
                   <button onClick={() => setEditing(i)} className="text-slate-500 hover:text-blue-400 p-1">
                     <Pencil size={11} />
                   </button>
                   <button onClick={async () => {
                     if (!window.confirm('Удалить?')) return;
                     await instInvApi.integrationDelete(instId, i.id); load();
                   }} className="text-slate-500 hover:text-red-400 p-1">
                     <Trash2 size={11} />
                   </button>
                 </div>
               </header>
               {(i.cred_user || i.notes) && (
                 <div className="text-[11px] text-slate-400 mt-1">
                   {i.cred_user && <span><span className="text-slate-600">creds:</span> {i.cred_user} <span className="font-mono text-slate-500">{i.cred_password}</span></span>}
                   {i.notes && <div className="italic">{i.notes}</div>}
                 </div>
               )}
             </div>
           ))}
         </div>
       )}
      {editing && (
        <IntegrationModal instId={instId} item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function IntegrationModal({ instId, item, onClose, onSaved }:
  { instId: string; item: InstIntegration | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<any>(item ? { ...item } : {
    kind: '', name: '', version: '', ip: '', port: null,
    cred_user: '', cred_password: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const upd = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload: any = { ...form };
      payload.port = payload.port === '' || payload.port == null ? null : Number(payload.port);
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      if (item) await instInvApi.integrationUpdate(instId, item.id, payload);
      else await instInvApi.integrationCreate(instId, payload);
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 py-10" onClick={onClose}>
      <form onSubmit={submit} className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg mx-4"
            onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between">
          <h3 className="text-lg font-bold text-white">{item ? 'Изменить интеграцию' : 'Новая интеграция'}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </header>
        <div className="p-4 grid grid-cols-2 gap-3">
          <Fld label="Вид (kind)">
            <Inp v={form.kind} k="kind" upd={upd} placeholder="DLP / antivirus / SSO / ..." />
          </Fld>
          <Fld label="Название *"><Inp v={form.name} k="name" upd={upd} required /></Fld>
          <Fld label="Версия"><Inp v={form.version} k="version" upd={upd} /></Fld>
          <Fld label="IP"><Inp v={form.ip} k="ip" upd={upd} /></Fld>
          <Fld label="Port"><Inp v={form.port} k="port" upd={upd} type="number" /></Fld>
          <div />
          <Fld label="User"><Inp v={form.cred_user} k="cred_user" upd={upd} /></Fld>
          <Fld label="Password"><Inp v={form.cred_password} k="cred_password" upd={upd} type="password" /></Fld>
          <Fld label="Заметки" colSpan={2}>
            <textarea value={form.notes || ''} onChange={e => upd('notes', e.target.value)} rows={2}
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white" />
          </Fld>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-800">
          <button type="button" onClick={onClose} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded text-sm">Отмена</button>
          <button type="submit" disabled={busy} className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-bold inline-flex items-center gap-1.5">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Сохранить
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Contacts ───────────────────────────────────────────────────────────────
function ContactsSection({ instId }: { instId: string }) {
  const [items, setItems] = useState<InstContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<InstContact | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await instInvApi.contacts(instId)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [instId]);

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={() => setEditing('new')}
          className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded text-xs font-bold">
          <Plus size={12} /> Контакт
        </button>
      </div>
      {loading ? <Loader2 className="animate-spin text-blue-400 mx-auto my-4" /> :
       items.length === 0 ? (
         <div className="text-xs text-slate-500 text-center py-4">Контактов нет.</div>
       ) : (
         <div className="space-y-2">
           {items.map(c => (
             <div key={c.id} className="bg-slate-950 border border-slate-800 rounded p-2.5 text-xs">
               <header className="flex justify-between items-baseline">
                 <div>
                   <b className="text-slate-200">{c.full_name}</b>
                   {c.role && <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-300">{c.role}</span>}
                 </div>
                 <div className="flex gap-1">
                   <button onClick={() => setEditing(c)} className="text-slate-500 hover:text-blue-400 p-1">
                     <Pencil size={11} />
                   </button>
                   <button onClick={async () => {
                     if (!window.confirm('Удалить?')) return;
                     await instInvApi.contactDelete(instId, c.id); load();
                   }} className="text-slate-500 hover:text-red-400 p-1">
                     <Trash2 size={11} />
                   </button>
                 </div>
               </header>
               <div className="text-[11px] text-slate-400 mt-0.5 space-x-3">
                 {c.phone && <span>📞 {c.phone}</span>}
                 {c.email && <span>✉ {c.email}</span>}
                 {c.telegram && <span>TG: {c.telegram}</span>}
                 {c.max_username && <span>MAX: {c.max_username}</span>}
               </div>
               {c.notes && <div className="text-[11px] text-slate-500 italic mt-1">{c.notes}</div>}
             </div>
           ))}
         </div>
       )}
      {editing && (
        <ContactModal instId={instId} item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function ContactModal({ instId, item, onClose, onSaved }:
  { instId: string; item: InstContact | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<any>(item ? { ...item } : {
    full_name: '', role: '', phone: '', email: '', telegram: '', max_username: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const upd = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload: any = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      if (item) await instInvApi.contactUpdate(instId, item.id, payload);
      else await instInvApi.contactCreate(instId, payload);
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 py-10" onClick={onClose}>
      <form onSubmit={submit} className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg mx-4"
            onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between">
          <h3 className="text-lg font-bold text-white">{item ? 'Изменить контакт' : 'Новый контакт'}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </header>
        <div className="p-4 grid grid-cols-2 gap-3">
          <Fld label="ФИО *"><Inp v={form.full_name} k="full_name" upd={upd} required /></Fld>
          <Fld label="Роль"><Inp v={form.role} k="role" upd={upd} placeholder="Admin AD / SMTP / Главный admin" /></Fld>
          <Fld label="Телефон"><Inp v={form.phone} k="phone" upd={upd} /></Fld>
          <Fld label="Email"><Inp v={form.email} k="email" upd={upd} type="email" /></Fld>
          <Fld label="Telegram (ник)"><Inp v={form.telegram} k="telegram" upd={upd} placeholder="@user" /></Fld>
          <Fld label="MAX (ник)"><Inp v={form.max_username} k="max_username" upd={upd} placeholder="@user" /></Fld>
          <Fld label="Заметки" colSpan={2}>
            <textarea value={form.notes || ''} onChange={e => upd('notes', e.target.value)} rows={2}
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white" />
          </Fld>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-800">
          <button type="button" onClick={onClose} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded text-sm">Отмена</button>
          <button type="submit" disabled={busy} className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-bold inline-flex items-center gap-1.5">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Сохранить
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Small UI helpers ───────────────────────────────────────────────────────
function Fld({ label, children, colSpan }: { label: string; children: React.ReactNode; colSpan?: number }) {
  return (
    <label className={`block ${colSpan === 2 ? 'col-span-2' : ''}`}>
      <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

function Inp({ v, k, upd, type = 'text', placeholder, required }:
  { v: any; k: string; upd: (k: string, v: any) => void;
    type?: string; placeholder?: string; required?: boolean }) {
  return (
    <input type={type} value={v ?? ''} required={required}
      placeholder={placeholder}
      onChange={e => upd(k, e.target.value)}
      className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white font-mono" />
  );
}
